import React from "react";
import { dateRange } from "./templateHelpers";

const VARIANT_STYLES = {
  modern: { font: "font-sans", nameSize: "text-2xl", tracking: "tracking-wider" },
  classic: { font: "font-serif", nameSize: "text-2xl", tracking: "tracking-wide" },
  minimal: { font: "font-sans", nameSize: "text-xl", tracking: "tracking-widest" },
  timeline_fallback: { font: "font-sans", nameSize: "text-2xl", tracking: "tracking-wider" },
};

const DEFAULT_ORDER = [
  "summary", "skills", "experience", "education", "projects", "internships",
  "certifications", "languages", "websites", "hobbies", "extracurricular", "custom_sections",
];

/** Modern/Classic/Minimal templates — the only 3 (of 22) that fully honor the learner's
 * custom drag-and-drop `section_order` (every section, freely interleaved) in the actual
 * rendered preview/PDF/DOCX. See ROADMAP.md: the other 19 templates keep their hand-designed
 * fixed layout via the shared <ExtraResumeSections> component instead. */
export default function SingleColumnTemplate({ draft, accent, variant = "modern" }) {
  const s = VARIANT_STYLES[variant] || VARIANT_STYLES.modern;
  const order = draft.section_order?.length ? draft.section_order : DEFAULT_ORDER;
  const heading = `text-xs font-bold uppercase ${s.tracking}`;

  const blocks = {
    summary: draft.summary && (
      <div key="summary" className="mt-4">
        <h2 className={heading} style={{ color: accent }}>Summary</h2>
        <p className="text-slate-700 mt-1">{draft.summary}</p>
      </div>
    ),
    skills: draft.skills.length > 0 && (
      <div key="skills" className="mt-4">
        <h2 className={heading} style={{ color: accent }}>Skills</h2>
        <p className="text-slate-700 mt-1">{draft.skills.join(" • ")}</p>
      </div>
    ),
    experience: draft.experience.length > 0 && (
      <div key="experience" className="mt-4">
        <h2 className={heading} style={{ color: accent }}>Experience</h2>
        {draft.experience.map((e, i) => (
          <div key={i} className="mt-2">
            <p className="font-semibold text-slate-800">{e.title} — {e.company} <span className="text-slate-400 font-normal italic">({dateRange(e.start_date, e.end_date, e.current)})</span></p>
            <ul className="list-disc list-inside text-slate-700 mt-0.5">{e.bullets.filter(Boolean).map((b, bi) => <li key={bi}>{b}</li>)}</ul>
          </div>
        ))}
      </div>
    ),
    education: draft.education.length > 0 && (
      <div key="education" className="mt-4">
        <h2 className={heading} style={{ color: accent }}>Education</h2>
        {draft.education.map((ed, i) => (
          <p key={i} className="text-slate-700 mt-1"><span className="font-semibold text-slate-800">{ed.degree} {ed.field}</span> — {ed.school} <span className="text-slate-400 italic">({ed.start_date} - {ed.end_date})</span></p>
        ))}
      </div>
    ),
    projects: draft.projects?.length > 0 && (
      <div key="projects" className="mt-4">
        <h2 className={heading} style={{ color: accent }}>Projects</h2>
        {draft.projects.map((p, i) => (
          <div key={i} className="mt-1.5">
            <p className="font-semibold text-slate-800">{p.title}{p.tech_stack ? ` — ${p.tech_stack}` : ""}</p>
            {(p.bullets || []).filter(Boolean).length > 0 && (
              <ul className="list-disc list-inside text-slate-700 mt-0.5">{p.bullets.filter(Boolean).map((b, bi) => <li key={bi}>{b}</li>)}</ul>
            )}
          </div>
        ))}
      </div>
    ),
    internships: draft.internships?.length > 0 && (
      <div key="internships" className="mt-4">
        <h2 className={heading} style={{ color: accent }}>Internships</h2>
        {draft.internships.map((it, i) => (
          <div key={i} className="mt-1.5">
            <p className="font-semibold text-slate-800">{it.title} — {it.company} <span className="text-slate-400 font-normal italic">({dateRange(it.start_date, it.end_date, it.current)})</span></p>
            <ul className="list-disc list-inside text-slate-700 mt-0.5">{(it.bullets || []).filter(Boolean).map((b, bi) => <li key={bi}>{b}</li>)}</ul>
          </div>
        ))}
      </div>
    ),
    certifications: draft.certifications?.length > 0 && (
      <div key="certifications" className="mt-4">
        <h2 className={heading} style={{ color: accent }}>Certifications</h2>
        {draft.certifications.map((c, i) => (
          <p key={i} className="text-slate-700 mt-1">{c.name}{c.issuer ? ` — ${c.issuer}` : ""} {c.date && <span className="text-slate-400 italic">({c.date})</span>}</p>
        ))}
      </div>
    ),
    languages: draft.languages?.length > 0 && (
      <div key="languages" className="mt-4">
        <h2 className={heading} style={{ color: accent }}>Languages</h2>
        <p className="text-slate-700 mt-1">{draft.languages.map((l) => `${l.name} (${l.proficiency})`).join("  •  ")}</p>
      </div>
    ),
    websites: draft.websites?.length > 0 && (
      <div key="websites" className="mt-4">
        <h2 className={heading} style={{ color: accent }}>Websites & Social</h2>
        <p className="text-slate-700 mt-1">{draft.websites.map((w) => w.label || w.url).join("  •  ")}</p>
      </div>
    ),
    hobbies: draft.hobbies?.length > 0 && (
      <div key="hobbies" className="mt-4">
        <h2 className={heading} style={{ color: accent }}>Hobbies</h2>
        <p className="text-slate-700 mt-1">{draft.hobbies.join("  •  ")}</p>
      </div>
    ),
    extracurricular: draft.extracurricular?.length > 0 && (
      <div key="extracurricular" className="mt-4">
        <h2 className={heading} style={{ color: accent }}>Extra-Curricular Activities</h2>
        <p className="text-slate-700 mt-1">{draft.extracurricular.join("  •  ")}</p>
      </div>
    ),
    custom_sections: (draft.custom_sections || []).filter((cs) => cs.title || cs.items?.length).length > 0 && (
      <React.Fragment key="custom_sections">
        {draft.custom_sections.filter((cs) => cs.title || cs.items?.length).map((cs, ci) => (
          <div key={ci} className="mt-4">
            <h2 className={heading} style={{ color: accent }}>{cs.title || "Additional Section"}</h2>
            {cs.items?.filter(Boolean).length > 0 && (
              <ul className="list-disc list-inside text-slate-700 mt-0.5">{cs.items.filter(Boolean).map((it, ii) => <li key={ii}>{it}</li>)}</ul>
            )}
          </div>
        ))}
      </React.Fragment>
    ),
  };

  return (
    <div className={`bg-white text-slate-900 rounded-xl p-8 shadow-2xl text-[13px] leading-snug ${s.font}`} data-testid="resume-live-preview">
      <h1 className={`${s.nameSize} font-bold`} style={{ color: accent }}>{draft.full_name || "Your Name"}</h1>
      <p className="text-slate-500 text-xs mt-0.5">{[draft.contact_email, draft.contact_phone, draft.location].filter(Boolean).join(" | ")}</p>
      {draft.target_role && <p className="text-slate-600 text-xs mt-0.5">{draft.target_role}</p>}
      {order.map((key) => blocks[key] || null)}
    </div>
  );
}
